import * as Sentry from '@sentry/react';
import {
  Container,
  getOriginalMailId,
  getRetryDelay,
  logError,
  logInfo,
  maskEmail,
  sleep,
} from '@utils-global';
import { AxiosError, AxiosResponse } from 'axios';
import { routes } from '../constants';
import { handleCaeChallenge } from './handleCaeChallenge';
import { isTransientError } from './helpers';

const MAX_RETRY_ATTEMPTS = 3;

/**
 * Retrieves email MIME content from Microsoft Graph API
 *
 * PARAMETERS:
 * @param mailId - Normalized mail ID (URL-safe base64 format: / → -, + → _)
 * @param userEmail - User's email address for Graph API call
 * @param isSharedMailbox - Whether accessing a shared mailbox (affects token scopes)
 *
 * RETURNS:
 * @returns Promise<AxiosResponse<string>> - MIME content as string
 *
 * ERROR HANDLING:
 * - CAE challenges: Automatically refresh token and retry
 * - Transient errors: Retry up to MAX_RETRY_ATTEMPTS times with exponential backoff
 * - Permanent errors: Fail immediately with detailed error info
 *
 * THROWS:
 * - Original error if permanent
 * - Last retry error if all transient retry attempts exhausted
 */
export const getMailMIME = async (
  mailId: string,
  userEmail: string,
  isSharedMailbox: boolean = false,
): Promise<AxiosResponse<string>> => {
  const originalMailId = getOriginalMailId();

  Sentry.addBreadcrumb({
    category: 'graph-api',
    level: 'info',
    message: 'Attempting to get mail MIME',
    data: {
      normalizedMailId: mailId,
      originalMailId,
      userEmail: maskEmail(userEmail),
      isSharedMailbox,
    },
  });

  try {
    const res = await Container.graphClient.get(routes.office.getMailMIME(mailId, userEmail));

    Sentry.addBreadcrumb({
      category: 'graph-api',
      level: 'info',
      message: 'Successfully retrieved mail MIME',
      data: { normalizedMailId: mailId },
    });

    return res;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorData = axiosError.response?.data as { error?: { code?: string; message?: string } };
    const errorCode = errorData?.error?.code;
    const errorMessage = errorData?.error?.message;
    const statusCode = axiosError.response?.status;

    Sentry.addBreadcrumb({
      category: 'graph-api',
      level: 'error',
      message: 'Failed to get mail MIME',
      data: {
        normalizedMailId: mailId,
        errorCode,
        errorMessage,
        statusCode,
        errorType: axiosError.code,
      },
    });

    // CAE (Continuous Access Evaluation) requires token refresh with claims
    if (error.isCaeChallenge && error.claims) {
      Sentry.addBreadcrumb({
        category: 'graph-api',
        level: 'info',
        message: 'CAE challenge detected - attempting token refresh',
        data: { claims: error.claims },
      });

      const newToken = await handleCaeChallenge(error.claims, isSharedMailbox);

      if (newToken) {
        try {
          const res = await Container.graphClient.get(routes.office.getMailMIME(mailId, userEmail));

          Sentry.addBreadcrumb({
            category: 'graph-api',
            level: 'info',
            message: 'CAE challenge resolved - retry succeeded',
          });

          return res;
        } catch (retryError) {
          logError('CAE retry failed:', retryError);

          Sentry.addBreadcrumb({
            category: 'graph-api',
            level: 'error',
            message: 'CAE challenge retry failed',
          });

          throw retryError;
        }
      } else {
        Sentry.addBreadcrumb({
          category: 'graph-api',
          level: 'error',
          message: 'Failed to refresh token for CAE challenge',
        });

        throw error;
      }
    }

    // Check if this is a transient error worth retrying
    const isTransient = isTransientError(axiosError);

    if (!isTransient) {
      Sentry.addBreadcrumb({
        category: 'graph-api',
        level: 'info',
        message: 'Permanent error - skipping retry',
        data: { errorCode, statusCode },
      });

      throw error;
    }

    // Retry transient errors with exponential backoff
    Sentry.addBreadcrumb({
      category: 'graph-api',
      level: 'info',
      message: 'Transient error - will retry with backoff',
      data: { errorCode, statusCode, maxRetries: MAX_RETRY_ATTEMPTS },
    });

    let attempt = 1;
    while (attempt <= MAX_RETRY_ATTEMPTS) {
      const delay = getRetryDelay(attempt);

      logInfo(`Retry ${attempt}/${MAX_RETRY_ATTEMPTS} after ${delay}ms`, {
        errorCode,
        statusCode,
      });

      // eslint-disable-next-line no-await-in-loop
      await sleep(delay);

      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await Container.graphClient.get(routes.office.getMailMIME(mailId, userEmail));

        Sentry.addBreadcrumb({
          category: 'graph-api',
          level: 'info',
          message: `Retry ${attempt} succeeded`,
          data: { attempt, originalError: { errorCode, statusCode } },
        });

        return res;
      } catch (retryError) {
        const retryAxiosError = retryError as AxiosError;
        const retryErrorData = retryAxiosError.response?.data as {
          error?: { code?: string; message?: string };
        };
        const retryErrorCode = retryErrorData?.error?.code;
        const retryStatusCode = retryAxiosError.response?.status;

        // Last attempt failed - log detailed comparison
        if (attempt === MAX_RETRY_ATTEMPTS) {
          logError('All retries exhausted', {
            originalError: { errorCode, errorMessage, statusCode },
            lastRetryError: { errorCode: retryErrorCode, statusCode: retryStatusCode },
          });

          Sentry.addBreadcrumb({
            category: 'graph-api',
            level: 'error',
            message: 'All retry attempts exhausted',
            data: {
              totalAttempts: MAX_RETRY_ATTEMPTS,
              originalError: { errorCode, errorMessage, statusCode },
              lastRetryError: { errorCode: retryErrorCode, statusCode: retryStatusCode },
            },
          });

          throw retryError;
        }

        // Continue to next retry
        attempt += 1;
      }
    }

    throw error;
  }
};
